// Owner: shell-nav (wave 2). The navigation model shared by the API (`GET /nav`, the universal search
// providers) and the web shell: the rail DTO, pinned and recent items, the member's rail state and the
// settings page registry (the one list the settings rail, ⌘K and the mobile menu read).
import { z } from 'zod';
import { ObjectColor, ObjectIcon } from './colors';

export const BadgeTone = z.enum(['accent', 'plain', 'amber', 'red']);
export type BadgeTone = z.infer<typeof BadgeTone>;

export const NavBadge = z.object({ value: z.string(), tone: BadgeTone });
export type NavBadge = z.infer<typeof NavBadge>;

export const NavObject = z.object({
  key: z.string(),
  label: z.string(),
  icon: ObjectIcon,
  color: ObjectColor,
});
export type NavObject = z.infer<typeof NavObject>;
export const NavApp = z.object({ key: z.string(), label: z.string(), objects: z.array(NavObject) });
export type NavApp = z.infer<typeof NavApp>;
/** Legacy favourite (a pinned saved view). Kept for older readers; the rail reads `pinned`. */
export const NavFavorite = z.object({ id: z.string(), label: z.string(), href: z.string() });

// -- Pinned and recent items -------------------------------------------------------------------------

/**
 * What can be pinned to the rail and shows in Recent. Views keep their membership in the views
 * module's favourites (the nav module only orders them); every other kind is stored by the nav module.
 */
export const NAV_ITEM_KINDS = ['view', 'record', 'page', 'deck', 'document', 'dashboard', 'inbox'] as const;
export const NavItemKind = z.enum(NAV_ITEM_KINDS);
export type NavItemKind = z.infer<typeof NavItemKind>;

/** A reference to something the rail can link to: `{ kind: 'record', id, objectKey }`. */
export const NavItemRef = z
  .object({
    kind: NavItemKind,
    /** Id of the record, page, deck, document template or dashboard; a view's id or key. */
    id: z.string().min(1).max(120),
    /** Object of a record or a view. */
    objectKey: z
      .string()
      .regex(/^[a-z][a-z0-9_]{0,62}$/)
      .optional(),
  })
  .strict()
  .refine((r) => (r.kind === 'record' || r.kind === 'view' ? Boolean(r.objectKey) : true), {
    message: 'Records and views need their object',
    path: ['objectKey'],
  });
export type NavItemRef = z.infer<typeof NavItemRef>;

/** A pinned or recent item as the rail shows it (titles resolved for the member, unreadable ones left out). */
export const NavItem = z.object({
  /** Stable key `<kind>:<id>`. */
  key: z.string(),
  kind: NavItemKind,
  id: z.string(),
  objectKey: z.string().nullable(),
  label: z.string(),
  /** In-app path the item opens. */
  href: z.string(),
  /** Icon name of `@dataroom/ui`. */
  icon: z.string(),
  /** Object colour of records and views (rail tint). */
  color: ObjectColor.nullable(),
});
export type NavItem = z.infer<typeof NavItem>;

export const RecentItem = NavItem.extend({ openedAt: z.string() });
export type RecentItem = z.infer<typeof RecentItem>;

/** The member's rail state, persisted per member and workspace. */
export const RailState = z.object({
  /** null = automatic (collapsed below 1200 px), true / false = the member's choice (⌘\). */
  collapsed: z.boolean().nullable(),
  /** App groups of the Data section the member folded. */
  collapsedGroups: z.array(z.string().max(80)).max(100),
});
export type RailState = z.infer<typeof RailState>;
export const RailStateInput = RailState.partial().strict();
export type RailStateInput = z.infer<typeof RailStateInput>;

/** What the member may reach from the rail (computed on the server from permissions). */
export const NavCapabilities = z.object({
  /** Builders (metadata.write or admin): Automations, "+ object", "Create workspace". */
  build: z.boolean(),
  admin: z.boolean(),
});
export type NavCapabilities = z.infer<typeof NavCapabilities>;

/**
 * GET /nav — everything the two navigation rails need.
 * `badges` keys: `inbox`, `automations`, and `settings.<item>` (members, objects, blueprints,
 * connections, quality, …). A key without a registered provider has no badge.
 */
/** A custom component that opens as an app (F20): the rail lists it under Apps and it lives at `/apps/<key>`. */
export const NavComponentApp = z.object({ key: z.string(), label: z.string(), icon: z.string() });
export type NavComponentApp = z.infer<typeof NavComponentApp>;

export const NavDTO = z.object({
  apps: z.array(NavApp),
  /** Published custom components that open as apps (rail section "Apps"). */
  componentApps: z.array(NavComponentApp).default([]),
  /** Deprecated: the pinned saved views (same as the `view` items of `pinned`). */
  favorites: z.array(NavFavorite),
  /** Pinned items in the member's order. */
  pinned: z.array(NavItem),
  badges: z.record(z.string(), NavBadge),
  rail: RailState,
  can: NavCapabilities,
  /** Where the installation runs and its version: for instance operators only (null for everybody else). */
  instance: z
    .object({
      version: z.string(),
      deployment: z.string(),
      region: z.string(),
      updateAvailable: z.string().nullable(),
    })
    .nullable(),
});
export type NavDTO = z.infer<typeof NavDTO>;

/** Icon of a nav item kind (records and views use their object's icon when known). */
export const NAV_KIND_ICONS: Record<NavItemKind, string> = {
  view: 'view',
  record: 'box',
  page: 'file',
  deck: 'play',
  document: 'form',
  dashboard: 'dashboards',
  inbox: 'inbox',
};

// -- Badges ------------------------------------------------------------------------------------------

/** The `NavDTO.badges` keys of the app rail: the Inbox, Automations and Data quality items. */
export const RAIL_BADGE_KEYS = ['inbox', 'automations', 'quality'] as const;

/**
 * The `NavDTO.badges` keys a settings page shows (plan §3.2). The settings pages themselves are
 * account-settings' model (`apps/web/src/features/account/settings-nav.ts`, whose `badge` is typed
 * on this list); `/nav` runs only the badge providers whose key is listed here or in `RAIL_BADGE_KEYS`.
 */
export const SETTINGS_BADGE_KEYS = ['settings.members', 'settings.objects', 'settings.blueprints'] as const;
export type SettingsBadgeKey = (typeof SETTINGS_BADGE_KEYS)[number];

/** Whether a member with these permissions may open a settings page. */
export function settingsPageVisible(
  page: { requires?: readonly string[] },
  permissions: readonly string[],
): boolean {
  if (permissions.includes('*') || permissions.includes('workspace.admin')) return true;
  return !page.requires || page.requires.some((p) => permissions.includes(p));
}

/** Builders: members who may change the data model and automations. */
export function isBuilder(permissions: readonly string[]): boolean {
  return (
    permissions.includes('*') ||
    permissions.includes('workspace.admin') ||
    permissions.includes('metadata.write')
  );
}

/** Members who may create and duplicate dashboards (API permission `dashboards.create`; admins hold all). */
export function canCreateDashboards(permissions: readonly string[]): boolean {
  return (
    permissions.includes('*') ||
    permissions.includes('workspace.admin') ||
    permissions.includes('dashboards.create')
  );
}
